'use client'

import Link from 'next/link'
import { useTranslations } from 'next-intl'
import { MailIcon, SendIcon } from 'lucide-react'
import { useForm } from 'react-hook-form'
import { z } from 'zod'
import { zodResolver } from '@hookform/resolvers/zod'
import { toast } from 'sonner'
import { Input } from '@/components/ui/input'
import { Button } from '@/components/ui/button'
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from '@/components/ui/form'
import { useForgotPassword } from '@/hooks/use-auth'
import { getErrorMessage, setValidationErrors } from '@/lib/utils'

const ForgotPasswordForm = () => {
  const t = useTranslations()
  const { mutate, isPending } = useForgotPassword()

  const formSchema = z.object({
    email: z.string().trim().email(t('invalid_email'))
  })

  const form = useForm<z.infer<typeof formSchema>>({
    resolver: zodResolver(formSchema),
    defaultValues: { email: '' }
  })

  const onSubmit = (data: z.infer<typeof formSchema>) => {
    mutate(
      { email: data.email, source: 'admin' },
      {
        onSuccess: (response: any) => {
          toast.success(response?.data?.message || t('password_reset_email_sent'))
          form.reset()
        },
        onError: (error: any) => {
          if (error.response?.status === 422) {
            setValidationErrors(error, form.setError)
          } else {
            toast.error(getErrorMessage(error, t('something_went_wrong')))
          }
        }
      }
    )
  }

  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(onSubmit)}>
        <div className="flex flex-col gap-3">
          <FormField
            control={form.control}
            name="email"
            render={({ field }) => (
              <FormItem>
                <FormLabel htmlFor="forgot-email" required>
                  {t('email')}
                </FormLabel>
                <FormControl>
                  <Input
                    id="forgot-email"
                    type="email"
                    autoComplete="email"
                    placeholder="mail@example.com"
                    icon={<MailIcon className="h-4 w-4" />}
                    autoFocus
                    disabled={isPending}
                    {...field}
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
          <Button type="submit" disabled={isPending} loading={isPending}>
            <SendIcon className="h-4 w-4" />
            {t('send_reset_link')}
          </Button>
          <div className="text-center text-sm">
            <Link href="/login" className="text-muted-foreground hover:underline">
              {t('back_to_login')}
            </Link>
          </div>
        </div>
      </form>
    </Form>
  )
}

export default ForgotPasswordForm
